import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { createJupiTheme, type ThemeMode } from '../theme'

// Light/dark, remembered per browser.
//
// Defaults to the OS preference on a first visit (a member who has set
// their phone to dark mode should not have to find a toggle to get it),
// then to whatever the member last chose explicitly. The member app and
// the admin app each keep their own copy of the preference -- they are
// separate origins once deployed under /admin/, so there is no single
// localStorage to share, and there is no reason an operator's admin theme
// should follow their member-app one anyway.
const STORAGE_KEY = 'jupi.themeMode'

interface ThemeModeState {
  mode: ThemeMode
  toggleMode: () => void
}

const ThemeModeContext = createContext<ThemeModeState | undefined>(undefined)

function storedMode(): ThemeMode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : null
  } catch {
    // Private browsing / storage disabled -- fall through to the OS
    // preference for this session instead of throwing.
    return null
  }
}

function systemMode(): ThemeMode {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => storedMode() ?? systemMode())

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // Nothing to persist to -- the toggle still works for this session.
    }
    // Lets native form controls (date/time pickers, scrollbars) pick a
    // matching chrome instead of staying light inside a dark page.
    document.documentElement.style.colorScheme = mode
  }, [mode])

  // Follow the OS setting live, but only until the member picks explicitly
  // -- once localStorage holds a value, that choice is deliberate and an
  // OS-level change (or the day/night auto-switch some phones do) must not
  // silently override it.
  useEffect(() => {
    if (storedMode()) return
    const query = globalThis.matchMedia?.('(prefers-color-scheme: dark)')
    if (!query) return
    const onChange = (e: MediaQueryListEvent) => setMode(e.matches ? 'dark' : 'light')
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const value = useMemo<ThemeModeState>(
    () => ({ mode, toggleMode: () => setMode((m) => (m === 'light' ? 'dark' : 'light')) }),
    [mode],
  )
  const theme = useMemo(() => createJupiTheme(mode), [mode])

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export function useThemeMode(): ThemeModeState {
  const context = useContext(ThemeModeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within a <ThemeModeProvider>')
  }
  return context
}
